import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Clock, User as UserIcon } from "lucide-react";

const ACTION_COLOR = {
  create: { bg: "bg-[#10B981]/10", fg: "text-[#10B981]", icon: Plus },
  update: { bg: "bg-[#F59E0B]/10", fg: "text-[#F59E0B]", icon: Pencil },
  delete: { bg: "bg-[#EF4444]/10", fg: "text-[#EF4444]", icon: Trash2 },
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState("all");
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = entityType === "all" ? {} : { entity_type: entityType };
      const r = await api.get("/audit-logs", { params });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [entityType]);

  return (
    <div className="p-10 max-w-6xl" data-testid="audit-log-page">
      <PageHeader overline="Compliance" title="Audit Log" testid="audit-header" />

      <div className="flex items-center justify-between mb-5 gap-4">
        <p className="text-xs text-[#6B7280] max-w-2xl">
          Chronological record of every create / update / delete action across entries, parties, categories, users, invites, and business settings.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <span className="overline">Filter by</span>
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger data-testid="audit-filter-entity" className="rounded-none h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All activity</SelectItem>
              <SelectItem value="entry">Entries</SelectItem>
              <SelectItem value="party">Parties</SelectItem>
              <SelectItem value="category">Categories</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="invite">Invites</SelectItem>
              <SelectItem value="business">Business</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border border-[#E3E3E3] bg-white" data-testid="audit-list">
        {loading && <div className="p-10 text-center text-sm text-[#6B7280]" data-testid="audit-loading">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-16 text-center text-sm text-[#6B7280]" data-testid="audit-empty">
            No activity recorded yet for this filter.
          </div>
        )}
        {!loading && items.map((it) => {
          const cfg = ACTION_COLOR[it.action] || ACTION_COLOR.update;
          const Icon = cfg.icon;
          const isExpanded = !!expanded[it.id];
          return (
            <div key={it.id} className="border-b border-[#E3E3E3] last:border-b-0" data-testid={`audit-row-${it.id}`}>
              <button
                type="button"
                onClick={() => setExpanded((m) => ({ ...m, [it.id]: !m[it.id] }))}
                className="w-full flex items-start gap-3 p-4 hover:bg-[#F9FAFB] text-left"
              >
                <div className={`p-1.5 ${cfg.bg} ${cfg.fg} shrink-0`}><Icon className="h-3.5 w-3.5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-wider font-bold ${cfg.fg}`}>{it.action}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">{it.entity_type}</span>
                  </div>
                  <div className="text-sm text-[#111827] mt-0.5 truncate">{it.summary || "—"}</div>
                  <div className="text-xs text-[#6B7280] mt-1 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><UserIcon className="h-3 w-3" /> {it.user_name || it.user_email || "—"}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTime(it.created_at)}</span>
                  </div>
                </div>
                <div className="text-[10px] text-[#9CA3AF] uppercase shrink-0 mt-1">{isExpanded ? "Hide" : "Details"}</div>
              </button>
              {isExpanded && (
                <div className="bg-[#F9FAFB] border-t border-[#E3E3E3] p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`audit-detail-${it.id}`}>
                  <div>
                    <div className="overline mb-1">Before</div>
                    <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-white border border-[#E3E3E3] p-2 max-h-60 overflow-auto">
                      {it.before ? JSON.stringify(it.before, null, 2) : "—"}
                    </pre>
                  </div>
                  <div>
                    <div className="overline mb-1">After</div>
                    <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-white border border-[#E3E3E3] p-2 max-h-60 overflow-auto">
                      {it.after ? JSON.stringify(it.after, null, 2) : "—"}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
